// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import { Link, useParams } from "react-router-dom";
import moment from "moment";
import TextArea from "antd/es/input/TextArea";
import { useSelector } from "react-redux";
import { Form } from "antd";
import clsx from "clsx";
import { ColumnsType } from "antd/es/table";

// Style Imports
import "./take-cash-transfer-action.scss";

// Local Imports
import CustomModal from "../../../../components/custom-modal/custom-modal";
import CollapseComponent from "../../../../components/collapse.component";
import BreadcrumbComponent from "../../../../components/breadcrumb-component/breadcrumb-component";
import TakeActionForm from "../take-action-form/take-action-form";
import Label from "../../../../components/label/label.component";
import CustomInput from "../../../../components/custom-input/custom-input.component";
import { formatNumber } from "../../../../utils/helper/functions";
import CustomTable from "../../../../components/custom-table/custom-table";
import CashTransferService from "../../cash-transfer.service";

const groups: any = {
  OPS: "Operation",
  AMD: "AMD",
  WMD: "WMD",
};

const TakeCashTransferAction: React.FC = (): ReactElement => {
  const user = useSelector((state: any) => state?.user);
  const [form] = Form.useForm();
  const takeCashTransferActionService = new CashTransferService();
  const { cashTransferId }: { [key: string]: string | undefined } = useParams();
  const [openApproveModal, setOpenApproveModal] = useState<boolean>(false);
  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);
  const [cashTransferDetailsData, setCashTransferDetailsData] = useState<any>();

  useEffect(() => {
    fetchcashTransferDetailsData();
  }, []);

  const onApproveCashTransfer = async (values: any) => {
    const response: string | undefined =
      await takeCashTransferActionService.takeAction(
        cashTransferId,
        "approve",
        values?.statusReason,
        values?.notes,
        values?.transactionId
      );
    if (response === "success") {
      fetchcashTransferDetailsData();
      setOpenApproveModal(false);
    }
  };

  const onRejectCashTransfer = async (values: any) => {
    const response: string | undefined =
      await takeCashTransferActionService.takeAction(
        cashTransferId,
        "reject",
        values?.statusReason,
        values?.notes,
        values?.transactionId
      );
    if (response === "success") {
      fetchcashTransferDetailsData();
      setOpenRejectModal(false);
    }
  };

  const fetchcashTransferDetailsData = async (): Promise<void> => {
    const data = await takeCashTransferActionService.getCashTransferDetailsData(
      cashTransferId
    );

    if (data !== undefined) {
      setCashTransferDetailsData(data);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      render: (_: any, __: any, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "original amount",
      dataIndex: "originalAmount",
      key: "originalAmount",
      fixed: "left",
      render: (_: any, record: any) => (
        <span>{formatNumber(record?.originalAmount)}</span>
      ),
    },
    {
      title: "Original currency",
      dataIndex: "originalCurrency",
      key: "originalCurrency",
      fixed: "left",
      render: (_: any, record: any) => <span>{record?.originalCurrency}</span>,
    },
    {
      title: "Converted amount",
      dataIndex: "convertedAmount",
      key: "convertedAmount",
      fixed: "left",
      render: (_: any, record: any) => (
        <span>{formatNumber(record?.convertedAmount)}</span>
      ),
    },
    {
      title: "Converted currency",
      dataIndex: "convertedCurrency",
      key: "convertedCurrency",
      fixed: "left",
      render: (_: any, record: any) => <span>{record?.convertedCurrency}</span>,
    },
    {
      title: "Full amount",
      dataIndex: "fullAmount",
      key: "fullAmount",
      fixed: "left",
      render: (_: any, record: any) => (
        <span>{record?.fullAmount ? "Yes" : "No"}</span>
      ),
    },
  ];

  return (
    cashTransferDetailsData && (
      <section className="flex flex-col">
        <BreadcrumbComponent />
        <div className="flex flex-col pb-7 flex-grow">
          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-xl font-semibold">Portfolio details</h3>
              </div>
            }
          >
            <div className="flex gap-5 flex-wrap mb-5">
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  {cashTransferDetailsData?.targetPortfolioId
                    ? "From portfolio#"
                    : "Portfolio#"}
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {cashTransferDetailsData?.sourcePortfolio?.accountCode}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio type
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {cashTransferDetailsData?.sourcePortfolio?.managementType}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio status
                </p>
                <span
                  className={clsx(
                    "text-base font-semibold w-fit px-3 py-1 rounded-md text-white",
                    cashTransferDetailsData?.sourcePortfolio?.status ===
                      "Opened"
                      ? "bg-green-100"
                      : cashTransferDetailsData?.sourcePortfolio?.status ===
                        "Rejected"
                      ? "bg-red-200"
                      : "bg-yellow-500"
                  )}
                >
                  {cashTransferDetailsData?.sourcePortfolio?.status ===
                  "Pending"
                    ? "New"
                    : cashTransferDetailsData?.sourcePortfolio?.status}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio currency
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {cashTransferDetailsData?.sourcePortfolio
                    ?.portfolioCurrency || "-"}
                </span>
              </div>
              {cashTransferDetailsData?.sourcePortfolio?.openDate && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Open date
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {moment(
                      cashTransferDetailsData?.sourcePortfolio?.openDate
                    ).format("DD MMM, YYYY")}
                  </span>
                </div>
              )}
              <br />

              {cashTransferDetailsData?.targetPortfolioId && (
                <>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      To portfolio#
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {cashTransferDetailsData?.targetPortfolio?.accountCode}
                    </span>
                  </div>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Portfolio type
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {cashTransferDetailsData?.targetPortfolio?.managementType}
                    </span>
                  </div>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Portfolio status
                    </p>
                    <span
                      className={clsx(
                        "text-base font-semibold w-fit px-3 py-1 rounded-md text-white",
                        cashTransferDetailsData?.targetPortfolio?.status ===
                          "Opened"
                          ? "bg-green-100"
                          : cashTransferDetailsData?.targetPortfolio?.status ===
                            "Rejected"
                          ? "bg-red-200"
                          : "bg-yellow-500"
                      )}
                    >
                      {cashTransferDetailsData?.targetPortfolio?.status ===
                      "Pending"
                        ? "New"
                        : cashTransferDetailsData?.targetPortfolio?.status}
                    </span>
                  </div>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Portfolio currency
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {cashTransferDetailsData?.targetPortfolio
                        ?.portfolioCurrency || "-"}
                    </span>
                  </div>
                  {cashTransferDetailsData?.targetPortfolio?.openDate && (
                    <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Open date
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {moment(
                          cashTransferDetailsData?.targetPortfolio?.openDate
                        ).format("DD MMM, YYYY")}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </CollapseComponent>

          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-xl font-semibold">
                  {cashTransferDetailsData?.targetPortfolioId
                    ? "Cash transfer details"
                    : "Fx conversion details"}
                  {cashTransferDetailsData?.status === "Approved" ||
                  cashTransferDetailsData?.status === "Rejected" ? (
                    <span
                      className={clsx(
                        "text-base font-semibold w-fit px-3 py-2 ms-3 rounded-md  text-white",
                        cashTransferDetailsData?.status === "Approved"
                          ? "bg-green-100"
                          : "bg-red-200"
                      )}
                    >
                      {cashTransferDetailsData?.status}
                    </span>
                  ) : (
                    ""
                  )}
                </h3>
              </div>
            }
          >
            <div className="flex gap-5 flex-wrap mb-5">
              {cashTransferDetailsData?.fromAvailableCash?.length ? (
                <div className="w-full flex flex-col mb-3">
                  <div className="w-full">
                    <CustomTable
                      columns={columns}
                      data={cashTransferDetailsData?.fromAvailableCash}
                      showPagination={false}
                    />
                  </div>
                </div>
              ) : (
                ""
              )}
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  All cash
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {cashTransferDetailsData?.fullAmount ? "Yes" : "No"}
                </span>
              </div>

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Request date
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {moment(cashTransferDetailsData?.createdAt).format(
                    "DD MMM, YYYY hh:mm A"
                  )}
                </span>
              </div>

              {cashTransferDetailsData?.status !== "Pending" ? (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Prospero cash transfer transaction ID
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {cashTransferDetailsData?.transactionId || "-"}
                  </span>
                </div>
              ) : (
                ""
              )}
              {cashTransferDetailsData?.reviewHistory?.length
                ? cashTransferDetailsData?.reviewHistory?.map(
                    (item: any, index: number) => (
                      <div
                        className="flex w-full gap-5 flex-wrap mb-5"
                        key={index}
                      >
                        <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                          <p className=" text-black-300 text-xs font-normal mb-[10px]">
                            {`${groups[item.group]} team notes`}
                          </p>
                          <span className=" text-base font-semibold text-lightGray-200">
                            {item?.notes || "-"}
                          </span>
                        </div>
                        <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                          <p className=" text-black-300 text-xs font-normal mb-[10px]">
                            {`${groups[item.group]} team action by`}
                          </p>
                          <span className=" text-base font-semibold text-lightGray-200">
                            {item?.actionBy?.name}
                          </span>
                        </div>
                        <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                          <p className=" text-black-300 text-xs font-normal mb-[10px]">
                            {`${groups[item.group]} team action date`}
                          </p>
                          <span className=" text-base font-semibold text-lightGray-200">
                            {moment(item?.actionDate).format(
                              "DD MMM, YYYY hh:mm A"
                            )}
                          </span>
                        </div>
                      </div>
                    )
                  )
                : ""}
            </div>
            {cashTransferDetailsData?.status === "Pending" &&
              cashTransferDetailsData?.approvalStep === user?.withdrawGroup &&
              user?.permissions?.includes("request_withdrawal_approver") && (
                <div className="flex gap-7 justify-end items-center">
                  <button
                    onClick={() => setOpenRejectModal(true)}
                    className="rounded-lg bg-white w-1/2 md:w-fit px-0 md:px-14 py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => setOpenApproveModal(true)}
                    className="rounded-lg bg-blue-500 w-1/2 md:w-fit px-0 md:px-14 py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white"
                  >
                    {cashTransferDetailsData?.targetPortfolioId
                      ? "Approve cash transfer"
                      : "Approve fx conversion"}
                  </button>
                </div>
              )}
          </CollapseComponent>
        </div>

        <CustomModal
          isVisible={openApproveModal}
          centered
          width={450}
          onClose={() => {
            setOpenApproveModal(false);
          }}
        >
          <TakeActionForm>
            <Form
              form={form}
              onFinish={onApproveCashTransfer}
              scrollToFirstError
            >
              {user?.withdrawGroup === "OPS" && (
                <div className="fields flex flex-col">
                  <Label text="Prospero cash transfer transaction ID" />
                  <Form.Item
                    name="transactionId"
                    rules={[
                      {
                        required: true,
                        message: `Please input cash transfer transaction ID!`,
                      },
                    ]}
                  >
                    <CustomInput
                      placeholder={`Enter cash transfer transaction ID`}
                      type="number"
                    />
                  </Form.Item>
                </div>
              )}
              <div className="fields flex flex-col">
                <Label
                  text={
                    user?.withdrawGroup === "AMD"
                      ? "AMD team notes"
                      : user?.withdrawGroup === "WMD"
                      ? "WMD team notes"
                      : "Operation team notes"
                  }
                />
                <Form.Item
                  name="notes"
                  rules={[
                    {
                      required: true,
                      message: `Please input notes!`,
                    },
                  ]}
                >
                  <TextArea rows={4} placeholder={`Enter notes`} />
                </Form.Item>
              </div>
              <div className="buttons-container flex align-center justify-space-between">
                <button
                  className="cancel"
                  type="button"
                  onClick={() => setOpenApproveModal(false)}
                >
                  Cancel
                </button>
                <button>Submit</button>
              </div>
            </Form>
          </TakeActionForm>
        </CustomModal>

        <CustomModal
          isVisible={openRejectModal}
          centered
          width={450}
          onClose={() => {
            setOpenRejectModal(false);
          }}
        >
          <TakeActionForm>
            <Form
              form={form}
              onFinish={onRejectCashTransfer}
              scrollToFirstError={true}
            >
              <div className="fields flex flex-col">
                <Label text="Rejection reason" />
                <Form.Item
                  name="notes"
                  rules={[
                    {
                      required: true,
                      message: `Please input Reason!`,
                    },
                  ]}
                >
                  <TextArea rows={4} placeholder={`Enter Reason`} />
                </Form.Item>
              </div>
              <div className="buttons-container flex align-center justify-space-between">
                <button
                  className="cancel"
                  type="button"
                  onClick={() => setOpenRejectModal(false)}
                >
                  Cancel
                </button>
                <button>Submit</button>
              </div>
            </Form>
          </TakeActionForm>
        </CustomModal>
      </section>
    )
  );
};

export default TakeCashTransferAction;
